import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  ShoppingCart, Plus, Minus, Trash2, CreditCard, 
  Banknote, Receipt, Package, Search
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// WebSocket URL helper
const getWebSocketUrl = () => {
  const wsBase = BACKEND_BASE.replace(/^http/i, 'ws');
  return `${wsBase}/api/ws`;
};

const AdminPOSPage = () => {
  const { token, admin } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [items, setItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [customerName, setCustomerName] = useState('');
  const [transactionRef, setTransactionRef] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchItems = async () => {
    try {
      const res = await fetch(`${API}/admin/pos/items`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setItems(await res.json());
      }
    } catch (error) {
      console.error('Failed to fetch items:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (item) => {
    const existing = cart.find(c => c.item_id === item.item_id);
    if (existing) {
      setCart(cart.map(c => 
        c.item_id === item.item_id 
          ? { ...c, quantity: c.quantity + 1 }
          : c
      ));
    } else {
      setCart([...cart, { ...item, quantity: 1 }]);
    }
  };

  const updateQuantity = (itemId, delta) => {
    setCart(cart.map(c => {
      if (c.item_id === itemId) {
        const newQty = c.quantity + delta;
        return newQty > 0 ? { ...c, quantity: newQty } : null;
      }
      return c;
    }).filter(Boolean));
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(c => c.item_id !== itemId));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const processOrder = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    if (!customerName.trim()) {
      toast.error('Customer name is required');
      return;
    }

    if (paymentMethod !== 'cash' && !transactionRef.trim()) {
      toast.error('Transaction reference number is required for non-cash payments');
      return;
    }

    setProcessing(true);
    try {
      const res = await fetch(`${API}/admin/pos/orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: cart.map(item => ({
            item_id: item.item_id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            category: item.category
          })),
          payment_method: paymentMethod,
          customer_name: customerName.trim(),
          transaction_ref: paymentMethod !== 'cash' ? transactionRef.trim() : null,
          customer_id: selectedCustomer?.user_id,
          notes: ''
        })
      });

      if (res.ok) {
        const order = await res.json();
        toast.success(`Order #${order.order_id.slice(-6)} completed`);
        setCart([]);
        setCustomerName('');
        setTransactionRef('');
        setSelectedCustomer(null);
        fetchItems(); // Refresh inventory
      } else {
        toast.error('Failed to process order');
      }
    } catch (error) {
      toast.error('Error processing order');
    } finally {
      setProcessing(false);
    }
  };

  const categories = [...new Set(items.map(i => i.category))];
  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !category || item.category === category;
    return matchesSearch && matchesCategory;
  });

  return (
    <AdminLayout>
      <div className="h-[calc(100vh-8rem)]">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
          {/* Items Grid */}
          <div className="lg:col-span-2 flex flex-col">
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="relative flex-1">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                <Input
                  placeholder="Search items..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={`pl-10 ${isDarkMode ? 'bg-slate-800/50 border-slate-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className={`h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-800/50 border-slate-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {filteredItems.map((item) => (
                    <Card 
                      key={item.item_id}
                      className={`${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} hover:border-emerald-500/50 cursor-pointer transition-colors`}
                      onClick={() => addToCart(item)}
                    >
                      <CardContent className="p-3">
                        <div className={`aspect-square ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg mb-2 flex items-center justify-center overflow-hidden`}>
                          {item.image ? (
                            <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <Package className={`h-8 w-8 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                          )}
                        </div>
                        <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} text-sm truncate`}>{item.name}</h3>
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-emerald-500 font-medium">₱{item.price}</span>
                          <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>{item.available_quantity} left</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Cart */}
          <div className="flex flex-col">
            <Card className={`${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} flex-1 flex flex-col`}>
              <CardHeader className="pb-2">
                <CardTitle className={`${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                  <ShoppingCart className="h-5 w-5" />
                  Current Order
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                {/* Cart Items */}
                <div className="flex-1 overflow-y-auto space-y-2 mb-4">
                  {cart.length === 0 ? (
                    <p className={`${isDarkMode ? 'text-slate-500' : 'text-gray-500'} text-center py-8`}>Cart is empty</p>
                  ) : (
                    cart.map((item) => (
                      <div key={item.item_id} className={`flex items-center gap-3 p-2 ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-100'} rounded`}>
                        <div className="flex-1 min-w-0">
                          <p className={`${isDarkMode ? 'text-white' : 'text-gray-900'} text-sm font-medium truncate`}>{item.name}</p>
                          <p className={`${isDarkMode ? 'text-slate-400' : 'text-gray-500'} text-xs`}>₱{item.price} each</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className={`h-8 w-8 p-0 ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-gray-300 hover:bg-gray-100'}`}
                            onClick={() => updateQuantity(item.item_id, -1)}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'} w-8 text-center font-semibold text-lg`}>{item.quantity}</span>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className={`h-8 w-8 p-0 ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-gray-300 hover:bg-gray-100'}`}
                            onClick={() => updateQuantity(item.item_id, 1)}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive" 
                            className="h-8 w-8 p-0"
                            onClick={() => removeFromCart(item.item_id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <p className="text-emerald-500 font-medium w-16 text-right">
                          ₱{(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    ))
                  )}
                </div>

                {/* Customer Name & Payment */}
                <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'} pt-4 space-y-4`}>
                  {/* Customer Name */}
                  <div>
                    <Label className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Customer Name *</Label>
                    <Input
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Enter customer name"
                      className={`mt-1 ${isDarkMode ? 'bg-slate-800/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                  </div>

                  {/* Payment Method */}
                  <div>
                    <Label className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Payment Method</Label>
                    <div className="grid grid-cols-3 gap-2 mt-1">
                      <Button
                        size="sm"
                        variant={paymentMethod === 'cash' ? 'default' : 'outline'}
                        onClick={() => { setPaymentMethod('cash'); setTransactionRef(''); }}
                        className={paymentMethod === 'cash' ? 'bg-emerald-600 text-white' : `${isDarkMode ? 'border-slate-600 text-white' : 'border-gray-300 text-gray-700'}`}
                      >
                        <Banknote className="h-4 w-4 mr-1" /> Cash
                      </Button>
                      <Button
                        size="sm"
                        variant={paymentMethod === 'card' ? 'default' : 'outline'}
                        onClick={() => setPaymentMethod('card')}
                        className={paymentMethod === 'card' ? 'bg-emerald-600 text-white' : `${isDarkMode ? 'border-slate-600 text-white' : 'border-gray-300 text-gray-700'}`}
                      >
                        <CreditCard className="h-4 w-4 mr-1" /> Card
                      </Button>
                      <Button
                        size="sm"
                        variant={paymentMethod === 'gcash' ? 'default' : 'outline'}
                        onClick={() => setPaymentMethod('gcash')}
                        className={paymentMethod === 'gcash' ? 'bg-emerald-600 text-white' : `${isDarkMode ? 'border-slate-600 text-white' : 'border-gray-300 text-gray-700'}`}
                      >
                        GCash
                      </Button>
                      <Button
                        size="sm"
                        variant={paymentMethod === 'maya' ? 'default' : 'outline'}
                        onClick={() => setPaymentMethod('maya')}
                        className={paymentMethod === 'maya' ? 'bg-emerald-600 text-white' : `${isDarkMode ? 'border-slate-600 text-white' : 'border-gray-300 text-gray-700'}`}
                      >
                        Maya
                      </Button>
                      <Button
                        size="sm"
                        variant={paymentMethod === 'qrph' ? 'default' : 'outline'}
                        onClick={() => setPaymentMethod('qrph')}
                        className={paymentMethod === 'qrph' ? 'bg-emerald-600 text-white' : `${isDarkMode ? 'border-slate-600 text-white' : 'border-gray-300 text-gray-700'}`}
                      >
                        QR Ph
                      </Button>
                      <Button
                        size="sm"
                        variant={paymentMethod === 'online_banking' ? 'default' : 'outline'}
                        onClick={() => setPaymentMethod('online_banking')}
                        className={paymentMethod === 'online_banking' ? 'bg-emerald-600 text-white' : `${isDarkMode ? 'border-slate-600 text-white' : 'border-gray-300 text-gray-700'}`}
                      >
                        Online Banking
                      </Button>
                    </div>
                  </div>

                  {/* Transaction Reference - show for non-cash payments */}
                  {paymentMethod !== 'cash' && (
                    <div>
                      <Label className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Transaction Reference # *</Label>
                      <Input
                        value={transactionRef}
                        onChange={(e) => setTransactionRef(e.target.value)}
                        placeholder="Enter transaction reference number"
                        className={`mt-1 ${isDarkMode ? 'bg-slate-800/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>
                  )}

                  {/* Total */}
                  <div className={`flex justify-between items-center py-3 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                    <span className={`${isDarkMode ? 'text-slate-300' : 'text-gray-700'} font-medium`}>Total</span>
                    <span className="text-2xl font-bold text-emerald-500">₱{cartTotal.toFixed(2)}</span>
                  </div>

                  {/* Process Button */}
                  <Button
                    className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white text-lg"
                    onClick={processOrder}
                    disabled={cart.length === 0 || processing || !customerName.trim()}
                  >
                    {processing ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <Receipt className="h-5 w-5 mr-2" /> Complete Order
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminPOSPage;